import type { Editor, Range } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TextSelection, type Transaction } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';
import { getChildTaskList } from './taskListItemNodeUtils';

/**
 * Whether the task item at the position is the list's only top-level task. The schema requires
 * the list to hold at least one task, so this one can be replaced but never simply removed.
 */
const isLastTopLevelTaskItem = (doc: ProseMirrorNode, position: number): boolean => {
    const $position = doc.resolve(position);

    return $position.depth === 1 && $position.parent.childCount === 1;
};

/**
 * Gets the document range a task item and its nested sub-tasks occupy for removal purposes.
 *
 * Deleting the only child of a nested list would leave an empty taskList, which the schema
 * forbids, so the range then covers the wrapping list instead.
 */
const getTaskItemRemovalRange = (doc: ProseMirrorNode, position: number): Range | null => {
    const taskItem = doc.nodeAt(position);

    if (!taskItem) return null;

    const $position = doc.resolve(position);
    const isOnlyChildOfList = $position.parent.childCount === 1;

    const from = isOnlyChildOfList ? $position.before() : position;
    const to = isOnlyChildOfList ? $position.after() : position + taskItem.nodeSize;

    return { from, to };
};

/**
 * Gets the document range required to remove a task item and its nested sub-tasks, or null
 * for the list's only top-level task, which cannot be deleted outright.
 *
 * Accepts a raw document so callers can re-derive the range against an in-progress
 * transaction (`transaction.doc`) before dispatching, not just the editor's current state.
 */
export const getTaskItemDeletionRange = (doc: ProseMirrorNode, position: number): Range | null => {
    if (!doc.nodeAt(position)) return null;
    if (isLastTopLevelTaskItem(doc, position)) return null;

    return getTaskItemRemovalRange(doc, position);
};

/**
 * Replaces the task item at the position with an empty task within the transaction.
 */
const replaceTaskItemWithEmptyTask = (transaction: Transaction, position: number): boolean => {
    const taskItem = transaction.doc.nodeAt(position);
    const emptyTaskItem = transaction.doc.type.schema.nodes[TiptapNodeType.taskItem].createAndFill();

    if (!taskItem || !emptyTaskItem) return false;

    transaction.replaceWith(position, position + taskItem.nodeSize, emptyTaskItem);

    return true;
};

/**
 * Removes a task item and its nested sub-tasks within the transaction, as a move does.
 *
 * The list's only top-level task is replaced with an empty one rather than removed, since the
 * schema requires a non-empty list. This mirrors the legacy task list, which gains an empty task
 * once every task has been moved out of it.
 */
export const removeTaskItem = (transaction: Transaction, position: number): boolean => {
    if (!transaction.doc.nodeAt(position)) return false;

    if (isLastTopLevelTaskItem(transaction.doc, position)) {
        return replaceTaskItemWithEmptyTask(transaction, position);
    }

    const removalRange = getTaskItemRemovalRange(transaction.doc, position);

    if (!removalRange) return false;

    transaction.delete(removalRange.from, removalRange.to);

    return true;
};

/**
 * Replaces the list's last remaining task with an empty one, so a titled list survives the
 * deletion — the schema requires the list to always hold at least one task. The caret moves to
 * the end of the title, mirroring the legacy task list.
 *
 * Returns false when there is no title text left to show, in which case the list itself is what
 * should be deleted (a hidden title counts as no title, matching legacy).
 */
const replaceLastTaskItemWithEmptyTask = (editor: Editor, position: number): boolean => {
    const { doc } = editor.state;
    const titleNode = doc.firstChild;

    if (titleNode?.type.name !== TiptapNodeType.taskListTitle) return false;
    if (!titleNode.textContent) return false;

    const { tr } = editor.state;

    if (!replaceTaskItemWithEmptyTask(tr, position)) return false;

    // The title is the document's first node, so its content ends just before its closing token.
    // The replaced task comes after it, so that position is unaffected by the replacement.
    tr.setSelection(TextSelection.near(tr.doc.resolve(titleNode.nodeSize - 1), -1));

    editor.view.dispatch(tr);

    // The replacement keeps the list's line count, so the synchroniser would debounce it
    editor.commands.flushPendingUpdate({});

    return true;
};

/**
 * Deletes the task item at the given position, along with any nested sub-tasks.
 *
 * The last remaining top-level task can't simply be removed, since the schema requires a
 * non-empty list: a titled list keeps its title and gets a fresh empty task in place of the
 * deleted one, while an untitled list has nothing left to show, so `onDeleteTaskList` is
 * invoked for the consumer to remove the whole list.
 */
export const deleteTaskItem = ({
    editor,
    position,
    onDeleteTask,
    onDeleteTaskList,
}: {
    editor: Editor;
    position: number;
    onDeleteTask?: () => void;
    onDeleteTaskList?: () => void;
}): void => {
    if (!editor.state.doc.nodeAt(position)) return;

    const deletionRange = getTaskItemDeletionRange(editor.state.doc, position);

    if (deletionRange) {
        editor.chain().deleteRange(deletionRange).flushPendingUpdate({}).run();
        onDeleteTask?.();
        return;
    }

    if (replaceLastTaskItemWithEmptyTask(editor, position)) {
        onDeleteTask?.();
        return;
    }

    // Land any pending edits before the list goes, so undoing its removal restores what the
    // user last saw rather than the content of the last debounced save
    editor.commands.flushPendingUpdate({});

    onDeleteTaskList?.();
};

/**
 * Collects the ranges to remove every completed task in a task list, and reports whether that
 * would empty the list.
 *
 * A completed task takes its whole subtree with it, so its sub-tasks are never visited. Emptying a
 * nested list is not allowed by the schema, so the wrapping list is removed in place of its tasks.
 */
const collectCompletedTaskRanges = (taskList: ProseMirrorNode, taskListPosition: number, ranges: Range[]): boolean => {
    let taskItemPosition = taskListPosition + 1;
    let isEmptiedByDeletions = true;

    taskList.forEach((taskItem) => {
        const position = taskItemPosition;
        taskItemPosition += taskItem.nodeSize;

        if (taskItem.attrs.checked) {
            ranges.push({ from: position, to: position + taskItem.nodeSize });
            return;
        }

        isEmptiedByDeletions = false;

        // Sub-tasks of an outstanding task can still be completed
        const nestedTaskList = getChildTaskList(taskItem);
        if (!nestedTaskList) return;

        // Offsets are relative to the task item's content, which starts one token in
        const nestedTaskListPosition = position + 1 + nestedTaskList.offset;
        const rangeCountBeforeNesting = ranges.length;

        if (!collectCompletedTaskRanges(nestedTaskList.node, nestedTaskListPosition, ranges)) return;

        ranges.length = rangeCountBeforeNesting;
        ranges.push({ from: nestedTaskListPosition, to: nestedTaskListPosition + nestedTaskList.node.nodeSize });
    });

    return isEmptiedByDeletions;
};

/**
 * Deletes every completed task in the editor's list, along with each one's sub-tasks, in a single
 * transaction so the whole removal is one undo step.
 *
 * A list where every task is completed keeps one empty outstanding task, since the schema requires
 * the list to be non-empty.
 */
export const deleteCompletedTaskItems = (editor: Editor): void => {
    const { doc } = editor.state;
    const taskList = getChildTaskList(doc);

    if (!taskList) return;

    // Document offsets are absolute positions
    const taskListPosition = taskList.offset;
    const ranges: Range[] = [];
    const isListEmptiedByDeletions = collectCompletedTaskRanges(taskList.node, taskListPosition, ranges);

    if (!ranges.length) return;

    const { tr } = editor.state;

    if (isListEmptiedByDeletions) {
        const emptyTaskItem = editor.schema.nodes[TiptapNodeType.taskItem].createAndFill();

        if (!emptyTaskItem) return;

        tr.replaceWith(taskListPosition + 1, taskListPosition + taskList.node.nodeSize - 1, emptyTaskItem);
    } else {
        // Applied last range first, so each range stays valid against the document it was resolved in
        ranges.reverse().forEach(({ from, to }) => tr.delete(from, to));
    }

    editor.view.dispatch(tr);
};
